import { useMemo, useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import styled from 'styled-components';
import { useVideoProjectCreation } from '@contexts/VideoProjectCreation.context';
import Select from '@components/form/Select';
import { MODEL_OPTIONS } from '@config/videoDetectionModelConfig';

const Panel = styled.div`
	padding: 1rem;
	background: rgba(${({ theme }) => theme.colors.borderLightRgb}, 0.1);
	border-radius: 0.375rem;
	border: 1px solid rgba(${({ theme }) => theme.colors.borderLightRgb}, 0.2);
`;

const PanelTitle = styled.h4`
	margin: 0 0 1rem 0;
	font-size: 1rem;
	font-weight: 600;
`;

const FormSection = styled.div`
	margin-bottom: 1rem;

	&:last-child {
		margin-bottom: 0;
	}
`;

const COUNTING_SCENARIOS = [
	{ value: 'fence_exit', label: 'Fence Exit' },
	{ value: 'moving_drone', label: 'Moving Drone' },
	{ value: 'stationary_drone', label: 'Stationary Drone' },
];

const COUNTING_METHODS = {
	unique_object: { value: 'unique_id', label: 'Unique Object' },
	virtual_line: { value: 'line_based', label: 'Virtual Line' },
	// counting_zone: { value: 'counting_zone', label: 'Counting Zone' }, // TODO: Enable when polygon drawing UI is ready
};

const ConfigurationOptions = () => {
	const { videoMetadata } = useVideoProjectCreation();
	const { control, watch, setValue } = useFormContext();

	const countingScenario = watch('countingScenario');
	const countingMethod = watch('countingMethod');
	const canUseVirtualLine = !!videoMetadata?.duration; // Only allow if video can be previewed

	// Determine available counting methods based on scenario
	const availableCountingMethods = useMemo(() => {
		if (countingScenario?.value === 'moving_drone') {
			return [COUNTING_METHODS.unique_object];
		}

		// If video can't be previewed, disable virtual_line
		if (!canUseVirtualLine) {
			return [
				COUNTING_METHODS.unique_object,
				{
					...COUNTING_METHODS.virtual_line,
					label: 'Virtual Line (Requires Video Preview)',
					isDisabled: true,
				},
				// TODO: Add counting_zone here when enabled
			];
		}

		return Object.values(COUNTING_METHODS);
	}, [countingScenario, canUseVirtualLine]);

	const isMovingDrone = countingScenario?.value === 'moving_drone';

	// Auto-set counting method when moving_drone is selected
	useEffect(() => {
		if (isMovingDrone && countingMethod?.value !== 'unique_id') {
			setValue('countingMethod', COUNTING_METHODS.unique_object);
		}
	}, [isMovingDrone, countingMethod, setValue]);

	// Auto-switch from virtual_line if video can't be previewed
	useEffect(() => {
		if (!canUseVirtualLine && countingMethod?.value === 'line_based') {
			setValue('countingMethod', COUNTING_METHODS.unique_object);
		}
	}, [canUseVirtualLine, countingMethod, setValue]);

	return (
		<Panel>
			<PanelTitle>Configuration</PanelTitle>

			<FormSection>
				<Controller
					name="modelOption"
					control={control}
					rules={{ required: 'Detection model is required' }}
					render={({ field, fieldState: { error } }) => (
						<Select
							{...field}
							id="modelOption"
							label="Detection Model *"
							options={MODEL_OPTIONS}
							placeholder="Select detection model"
							error={error?.message}
						/>
					)}
				/>
			</FormSection>

			<FormSection>
				<Controller
					name="countingScenario"
					control={control}
					rules={{ required: 'Counting scenario is required' }}
					render={({ field, fieldState: { error } }) => (
						<Select
							{...field}
							id="countingScenario"
							label="Counting Scenario *"
							options={COUNTING_SCENARIOS}
							placeholder="Select counting scenario"
							error={error?.message}
						/>
					)}
				/>
			</FormSection>

			{countingScenario && (
				<FormSection>
					<Controller
						name="countingMethod"
						control={control}
						rules={{ required: 'Counting method is required' }}
						render={({ field, fieldState: { error } }) => (
							<Select
								{...field}
								id="countingMethod"
								label="Counting Method *"
								options={availableCountingMethods}
								placeholder="Select counting method"
								isDisabled={isMovingDrone}
								error={error?.message}
								helperText={
									isMovingDrone
										? 'Moving drone only supports unique object method'
										: null
								}
							/>
						)}
					/>
				</FormSection>
			)}
		</Panel>
	);
};

export default ConfigurationOptions;
